import React, { FC, Fragment, useEffect, useState } from 'react';
import { TabContext, TabPanel } from '@mui/lab';
import { Badge, Box, Button, Tab, Tabs } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryPlanner from 'pages/transport/delivery-planner/delivery-planner.component';
import TransportSchedule from 'pages/transport/transport-schedule/transport-schedule.component';
import { useTranslation } from 'react-i18next';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintWaybills from './delivery-planner/print-waybills.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useLazyQuery } from '@apollo/client';
import { waybillCountTemplate } from 'apollo/templates/transport/query.graphql';

const Transport: FC = () => {
    const { t: translate } = useTranslation(['transport']);
    const navigate = useNavigate();
    const [tabState, setTabState] = useState('transport-schedule');
    const { tab } = useParams<{ tab: string }>();
    const [showWaybills, setShowWaybills] = useState(false);
    const [waybillCount, setWaybillCount] = useState(0);
    const [hasNshift, setHasNshift] = useState(false);
    const [hasDenthubTransport, setHasDenthubTransport] = useState(false);

    const [getWaybillCount] = useLazyQuery(waybillCountTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ waybillCount }) => {
            setWaybillCount(waybillCount);
        },
    });

    useEffect(() => {
        const lab = currentOrgVar();
        const hasNShiftProvider = lab.transportSettings?.nShiftTransport?.isEnabled || false;
        const hasDenthubTransport = lab.transportSettings?.denthubTransport?.isEnabled || false;
        setHasNshift(hasNShiftProvider);
        setHasDenthubTransport(hasDenthubTransport);
    }, []);

    useEffect(() => {
        handleTabChange(tab || 'transport-schedule');
        getWaybillCount();
    }, [tab]);

    const handleTabChange = (newTabState: string) => {
        if (tabState !== newTabState) {
            const url = `/transport/${newTabState}`;
            navigate(url);
            setTabState(newTabState);
        }
    };

    const handleAddTransport = (type: string) => {
        navigate(`/transport/add-transport/${type}`);
    };

    const handleShipmentCreate = () => {
        getWaybillCount();
    };

    return (
        <Fragment>
            <TabContext value={tabState}>
                <Box mb={2} display="flex" alignItems="center">
                    <Box flexGrow={1}>
                        <Tabs value={tabState} onChange={(_, value) => handleTabChange(value)}>
                            {/* Delivery planner tab twice to control the order of tabs */}
                            {hasNshift && !hasDenthubTransport ? (
                                <Tab label={translate('transport:DELIVERY_PLANNING')} value="delivery-planner" />
                            ) : undefined}
                            <Tab label={translate('transport:TRANSPORT_SCHEDULE')} value="transport-schedule" />
                            {hasNshift && hasDenthubTransport ? (
                                <Tab label={translate('transport:DELIVERY_PLANNING')} value="delivery-planner" />
                            ) : undefined}
                        </Tabs>
                    </Box>
                    <Box>
                        {tab === 'transport-schedule' ? (
                            <Fragment>
                                <Button
                                    variant="contained"
                                    startIcon={<LocalShippingIcon />}
                                    onClick={() => handleAddTransport('pickup')}
                                    sx={{ marginRight: 1 }}
                                >
                                    {translate('transport:PICKUP_FROM_CLINIC')}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<LocalShippingIcon />}
                                    onClick={() => handleAddTransport('delivery')}
                                >
                                    {translate('transport:DELIVERY_TO_CLINIC')}
                                </Button>
                            </Fragment>
                        ) : undefined}

                        {hasNshift && tab === 'delivery-planner' && !showWaybills ? (
                            <Button
                                variant="contained"
                                startIcon={<ReceiptLongIcon />}
                                onClick={() => setShowWaybills(true)}
                            >
                                <Badge badgeContent={waybillCount} color="error">
                                    {translate('transport:WAYBILLS')}
                                </Badge>
                            </Button>
                        ) : undefined}
                        {hasNshift && tab === 'delivery-planner' && showWaybills ? (
                            <Button
                                variant="contained"
                                startIcon={<LocalShippingIcon />}
                                onClick={() => setShowWaybills(false)}
                            >
                                {translate('transport:SHOW_DELIVERY_PLANNING')}
                            </Button>
                        ) : undefined}
                    </Box>
                </Box>
                <TabPanel value="transport-schedule" sx={{ padding: 0 }}>
                    <TransportSchedule />
                </TabPanel>
                {hasNshift ? (
                    <TabPanel value="delivery-planner" sx={{ padding: 0 }}>
                        {showWaybills ? <PrintWaybills /> : <DeliveryPlanner onShipmentCreate={handleShipmentCreate} />}
                    </TabPanel>
                ) : undefined}
            </TabContext>
        </Fragment>
    );
};

export default Transport;
